// src/pages/CompleteProfile.jsx
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getStudent, saveStudentProfile } from "../firebase/database";
import { storage } from "../firebase/config";
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";

const C = {
  bg: "#F7F8FC", card: "#FFFFFF", border: "#E4E7F0",
  accent: "#3D7EFF", text: "#111827", muted: "#6B7280", green: "#16A349"
};

export default function CompleteProfile({ studentId: propStudentId, isEmbedded = false }) {
  const { id: paramId } = useParams();
  const id = propStudentId || paramId;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [role, setRole] = useState("student"); // "student" or "staff"
  const [form, setForm] = useState({
    // Shared
    name: "", email: "", phone: "", familyPhone: "", dob: "", address: "", educationalQualification: "",
    religion: "", caste: "", casteCategory: "General", declaration: false,
    dateOfJoining: "",
    
    // Health Details
    weight: "", heightFeet: "", heightInches: "", bmi: "", dailyKcalTarget: "", gender: "Male",
    
    // Student specific
    fatherName: "", birthTime: "", birthPlace: "", 
    courseJoined: "", feesPaymentOption: "Full Payment", preferredBatchTime: "",
    referredBy: "",
    
    // Staff specific
    designation: "", employmentType: "Full Time", workingHours: "", 
    emergencyContact: "", previousExperience: "", salaryDetails: ""
  });

  const calculateHealth = (updatedForm) => {
    const w = parseFloat(updatedForm.weight);
    const hFt = parseFloat(updatedForm.heightFeet);
    const hIn = parseFloat(updatedForm.heightInches);
    
    if (w > 0 && hFt > 0) {
      // 1. Calculate BMI
      const totalInches = (hFt * 12) + (hIn || 0);
      const heightM = totalInches * 0.0254;
      const heightCm = heightM * 100;
      const bmi = (w / (heightM * heightM)).toFixed(1);
      
      // 2. Calculate Daily Kcal Target (BMR * 1.2 Activity Factor)
      let age = 25; // Default if DOB missing
      if (updatedForm.dob) {
        const birthDate = new Date(updatedForm.dob);
        age = new Date().getFullYear() - birthDate.getFullYear();
      }
      
      let bmr;
      if (updatedForm.gender === "Male") {
        bmr = (10 * w) + (6.25 * heightCm) - (5 * age) + 5;
      } else {
        bmr = (10 * w) + (6.25 * heightCm) - (5 * age) - 161;
      }
      
      const kcal = Math.round(bmr * 1.2); // Sedentary factor
      
      return { ...updatedForm, bmi, dailyKcalTarget: kcal };
    }
    return updatedForm;
  };

  const handleHealthChange = (key, val) => {
    setForm(prev => {
      const next = { ...prev, [key]: val };
      return calculateHealth(next);
    });
  };
  const [files, setFiles] = useState({ photo: null, idCard: null, marksheet: null, resume: null, paymentReceipt: null });
  const [signatureBase64, setSignatureBase64] = useState(null);

  useEffect(() => {
    getStudent(id).then(data => {
      if (data) {
        const profileRole = data.role || (id.startsWith("STAFF") ? "staff" : "student");
        setRole(profileRole);
        setForm(f => ({ 
          ...f, 
          ...data, 
          role: profileRole,
          roll: data.roll || id 
        }));
        if (data.signatureUrl) setSignatureBase64(data.signatureUrl);
      }
      setLoading(false);
    });
  }, [id]);

  const handleFile = (e, key) => {
    const file = e.target.files[0];
    if (!file) return;

    if (key === "signature") {
      const reader = new FileReader();
      reader.onloadend = () => {
        setSignatureBase64(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      setFiles(prev => ({ ...prev, [key]: file }));
    }
  };

  const uploadFile = async (file, folder) => {
    if (!file) return null;
    const r = storageRef(storage, `documents/${id}/${folder}_${Date.now()}`);
    await uploadBytes(r, file);
    return getDownloadURL(r);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.declaration) return alert("Please accept the declaration.");
    setSaving(true);
    try {
      const urls = {};
      for (const [key, file] of Object.entries(files)) {
        if (file) urls[`${key}Url`] = await uploadFile(file, key);
      }
      
      const profileData = { 
        ...form, 
        ...urls, 
        profileCompleted: true 
      };

      if (signatureBase64) {
        profileData.signatureUrl = signatureBase64;
      }

      await saveStudentProfile(id, profileData);
      alert("Profile updated successfully!");
      if (!isEmbedded) {
        if (id.startsWith("STAFF")) navigate("/admin");
        else navigate("/kiosk");
      }
    } catch (err) {
      alert("Error: " + err.message);
    }
    setSaving(false);
  };

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: C.text }}>Loading profile...</div>;

  const isStaff = role === "staff";

  const inp = (label, key, type = "text", options = null) => (
    <div style={{ marginBottom: 16 }}>
      <label style={{ fontSize: 13, color: C.muted, display: "block", marginBottom: 6, fontWeight: 500 }}>{label}</label>
      {options ? (
        <select value={form[key] || ""} onChange={e => setForm({ ...form, [key]: e.target.value })} style={{ width: "100%", padding: 10, borderRadius: 8, border: `1px solid ${C.border}`, outline: "none", background: "#fff", fontSize: 14 }}>
          {options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : (
        <input type={type} value={form[key] || ""} onChange={e => setForm({ ...form, [key]: e.target.value })} style={{ width: "100%", padding: 10, borderRadius: 8, border: `1px solid ${C.border}`, outline: "none", fontSize: 14 }} />
      )}
    </div>
  );

  const fileInp = (label, key) => (
    <div style={{ marginBottom: 16 }}>
      <label style={{ fontSize: 13, color: C.muted, display: "block", marginBottom: 6, fontWeight: 500 }}>{label}</label>
      <input type="file" onChange={e => handleFile(e, key)} style={{ fontSize: 13, color: C.muted }} />
      {form[`${key}Url`] && <div style={{ fontSize: 11, color: C.green, marginTop: 4 }}>Already uploaded ✓</div>}
    </div>
  );

  return (
    <div style={{ background: isEmbedded ? "transparent" : C.bg, minHeight: isEmbedded ? "auto" : "100vh", padding: isEmbedded ? "0" : "40px 20px", fontFamily: "'DM Sans', sans-serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap');`}</style>
      
      <div style={{ maxWidth: 840, margin: "0 auto", background: C.card, padding: isEmbedded ? "24px" : "36px", borderRadius: 16, border: isEmbedded ? "none" : `1px solid ${C.border}`, boxShadow: isEmbedded ? "none" : "0 4px 20px rgba(0,0,0,0.05)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 28 }}>
          <div>
            <h2 style={{ fontSize: 24, fontWeight: 700, color: C.text, marginBottom: 4 }}>
              {isEmbedded ? "My Profile Settings" : `Complete ${isStaff ? "Staff" : "Student"} Profile`}
            </h2>
            <p style={{ color: C.muted, fontSize: 14 }}>
              {isStaff ? `ID: ${form.roll}` : `Roll Number: #${form.roll}`} · {form.name}
            </p>
          </div>
          {!isEmbedded && (
            <button onClick={() => navigate(-1)} style={{ padding: "8px 16px", borderRadius: 8, border: `1px solid ${C.border}`, background: "none", color: C.muted, fontSize: 13, cursor: "pointer" }}>
              ← Back
            </button>
          )}
        </div>
        
        <form onSubmit={handleSubmit}>
          {/* Section: Personal Information */}
          <div style={{ fontSize: 14, fontWeight: 700, color: C.accent, marginBottom: 16, borderBottom: `1px solid ${C.border}`, paddingBottom: 8 }}>
            Personal Details & Security
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 24px" }}>
            {inp("Full Name", "name")}
            {inp("4-Digit Secure PIN", "pin", "password")}
            {isStaff ? inp("Spouse / Father's Name", "fatherName") : inp("Father's Name", "fatherName")}
            {inp("Email Address", "email", "email")}
            {inp("Mobile Number", "phone")}
            {inp("Family Mobile Number", "familyPhone")}
            {inp("Date of Birth", "dob", "date")}
            {!isStaff && inp("Birth Time", "birthTime", "time")}
            {!isStaff && inp("Birth Place", "birthPlace")}
            {inp("Religion", "religion")}
            {inp("Caste", "caste")}
            {inp("Caste Category", "casteCategory", "select", ["General", "OBC", "SC", "ST", "Other"])}
            {inp("Gender", "gender", "select", ["Male", "Female", "Other"])}
          </div>

          {/* Section: Health & Fitness */}
          <div style={{ fontSize: 14, fontWeight: 700, color: C.accent, marginTop: 12, marginBottom: 16, borderBottom: `1px solid ${C.border}`, paddingBottom: 8 }}>
            Health & Fitness Details
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0 24px" }}>
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 13, color: C.muted, display: "block", marginBottom: 6, fontWeight: 500 }}>Weight (kg)</label>
              <input type="number" step="0.1" value={form.weight || ""} onChange={e => handleHealthChange("weight", e.target.value)} style={{ width: "100%", padding: 10, borderRadius: 8, border: `1px solid ${C.border}`, outline: "none", fontSize: 14 }} />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 13, color: C.muted, display: "block", marginBottom: 6, fontWeight: 500 }}>Height (Feet)</label>
              <input type="number" value={form.heightFeet || ""} onChange={e => handleHealthChange("heightFeet", e.target.value)} style={{ width: "100%", padding: 10, borderRadius: 8, border: `1px solid ${C.border}`, outline: "none", fontSize: 14 }} />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 13, color: C.muted, display: "block", marginBottom: 6, fontWeight: 500 }}>Height (Inches)</label>
              <input type="number" value={form.heightInches || ""} onChange={e => handleHealthChange("heightInches", e.target.value)} style={{ width: "100%", padding: 10, borderRadius: 8, border: `1px solid ${C.border}`, outline: "none", fontSize: 14 }} />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 13, color: C.muted, display: "block", marginBottom: 6, fontWeight: 500 }}>Auto BMI</label>
              <input type="text" readOnly value={form.bmi || ""} style={{ width: "100%", padding: 10, borderRadius: 8, border: `1px solid ${C.border}`, outline: "none", fontSize: 14, background: "#f3f4f6", fontWeight: 700, color: C.accent }} />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 13, color: C.muted, display: "block", marginBottom: 6, fontWeight: 500 }}>Daily Kcal Target (Est.)</label>
              <input type="text" readOnly value={form.dailyKcalTarget ? `${form.dailyKcalTarget} kcal` : ""} style={{ width: "100%", padding: 10, borderRadius: 8, border: `1px solid ${C.border}`, outline: "none", fontSize: 14, background: "#f3f4f6", fontWeight: 700, color: C.green }} />
            </div>
          </div>

          {/* Section: Professional / Academic */}
          <div style={{ fontSize: 14, fontWeight: 700, color: C.accent, marginTop: 12, marginBottom: 16, borderBottom: `1px solid ${C.border}`, paddingBottom: 8 }}>
            {isStaff ? "Employment Details" : "Academic Details"}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 24px" }}>
            {isStaff ? inp("Designation", "designation") : inp("Course Joined", "courseJoined")}
            {isStaff 
              ? inp("Employment Type", "employmentType", "select", ["Full Time", "Part Time", "Contract", "Visiting"])
              : inp("Fees Payment Option", "feesPaymentOption", "select", ["Full Payment", "Installments", "Scholarship"])
            }
            {inp("Date of Joining", "dateOfJoining", "date")}
            {isStaff ? inp("Working Hours", "workingHours") : inp("Preferred Batch Time", "preferredBatchTime")}
            {isStaff ? inp("Emergency Contact", "emergencyContact") : inp("Referred By", "referredBy")}
            {isStaff && inp("Salary / Payment Details", "salaryDetails")}
          </div>

          <div style={{ marginBottom: 20 }}>
            <label style={{ fontSize: 13, color: C.muted, display: "block", marginBottom: 6, fontWeight: 500 }}>Permanent Address</label>
            <textarea value={form.address || ""} onChange={e => setForm({ ...form, address: e.target.value })} style={{ width: "100%", padding: 12, borderRadius: 8, border: `1px solid ${C.border}`, outline: "none", minHeight: 80, fontSize: 14, fontFamily: "inherit" }} />
          </div>

          {/* Section: Documentation */}
          <div style={{ fontSize: 14, fontWeight: 700, color: C.accent, marginTop: 12, marginBottom: 16, borderBottom: `1px solid ${C.border}`, paddingBottom: 8 }}>
            Documentation
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 24px", background: "#F9FAFB", padding: 24, borderRadius: 12, marginBottom: 28 }}>
            {fileInp("Profile Photo", "photo")}
            {fileInp("ID Proof (Aadhar/DL)", "idCard")}
            {fileInp("Educational Marksheet", "marksheet")}
            {isStaff ? fileInp("Resume / CV", "resume") : fileInp("Payment Receipt", "paymentReceipt")}
            {isStaff && (
              <div style={{ marginBottom: 16 }}>
                {fileInp("Signature (Digital/Scanned)", "signature")}
                {signatureBase64 && (
                  <div style={{ marginTop: 8, padding: 8, border: `1px dashed ${C.border}`, borderRadius: 8, background: "#fff", display: "inline-block" }}>
                    <img src={signatureBase64} alt="Signature Preview" style={{ maxHeight: 60, maxWidth: 200 }} />
                  </div>
                )}
              </div>
            )}
          </div>

          <div style={{ marginBottom: 28, display: "flex", alignItems: "flex-start", gap: 12, background: C.accent + "08", padding: 16, borderRadius: 10, border: `1px solid ${C.accent}22` }}>
            <input type="checkbox" id="decl" checked={form.declaration || false} onChange={e => setForm({ ...form, declaration: e.target.checked })} style={{ marginTop: 3, cursor: "pointer" }} />
            <label htmlFor="decl" style={{ fontSize: 13, color: C.text, lineHeight: 1.5, cursor: "pointer" }}>
              I hereby declare that all the information provided above is true and accurate to the best of my knowledge. I understand that any false statement may result in the termination of my {isStaff ? "employment" : "enrollment"}.
            </label>
          </div>

          <div style={{ display: "flex", gap: 16 }}>
            <button type="submit" disabled={saving} style={{ flex: 2, padding: 16, borderRadius: 12, background: C.accent, color: "#fff", border: "none", fontSize: 16, fontWeight: 600, cursor: "pointer", boxShadow: `0 4px 12px ${C.accent}44` }}>
              {saving ? "Saving Profile..." : "Submit & Save Full Profile"}
            </button>
            <button type="button" onClick={() => navigate(-1)} style={{ flex: 1, padding: 16, borderRadius: 12, background: "none", border: `1px solid ${C.border}`, color: C.muted, fontSize: 16, fontWeight: 500, cursor: "pointer" }}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
